// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { Provider } from './providerApi';

export interface BrokerageProvider {
  id?: string;
  brokerageId: string;
  providerId: string;
  provider?: Provider;
  isEnabled: boolean;
  customTemplateId?: string;
  customTemplateName?: string;
  usingDefaultTemplate?: boolean;
  customConfig?: Record<string, unknown>;
  policyTypeCount?: number;
  enabledPolicyTypeCount?: number;
  emailSubjectTemplate?: string;
  emailBodyTemplateHtml?: string;
  emailBodyTemplateText?: string;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
  agencyCode?: string;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
  first: boolean;
  last: boolean;
}

const getApiBase = (brokerageId: string) => `/api/brokerages/${brokerageId}/providers`;

export const brokerageProviderApi = {
  async getAll(
    brokerageId: string,
    params?: {
      isEnabled?: boolean;
      page?: number;
      size?: number;
    }
  ): Promise<PageResponse<BrokerageProvider>> {
    const queryParams = new URLSearchParams();
    if (params?.isEnabled !== undefined) queryParams.append('isEnabled', String(params.isEnabled));
    if (params?.page !== undefined) queryParams.append('page', String(params.page));
    if (params?.size !== undefined) queryParams.append('size', String(params.size));

    const queryString = queryParams.toString();
    const suffix = queryString ? `?${queryString}` : '';
    const url = `${getApiBase(brokerageId)}${suffix}`;
    const response = await fetch(url, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch brokerage providers');
    }
    return response.json();
  },

  async getById(brokerageId: string, providerId: string): Promise<BrokerageProvider> {
    const response = await fetch(`${getApiBase(brokerageId)}/${providerId}`, {
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to fetch brokerage provider');
    }
    return response.json();
  },

  async enable(brokerageId: string, providerId: string): Promise<BrokerageProvider> {
    const response = await fetch(`${getApiBase(brokerageId)}/${providerId}/enable`, {
      method: 'POST',
      credentials: 'include'
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to enable provider');
    }
    return response.json();
  },

  async disable(brokerageId: string, providerId: string): Promise<BrokerageProvider> {
    const response = await fetch(`${getApiBase(brokerageId)}/${providerId}/disable`, {
      method: 'POST',
      credentials: 'include'
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to disable provider');
    }
    return response.json();
  },

  async setCustomTemplate(
    brokerageId: string,
    providerId: string,
    templateId: string | null
  ): Promise<BrokerageProvider> {
    const queryParams = new URLSearchParams();
    if (templateId) {
      queryParams.append('templateId', templateId);
    }

    const queryString = queryParams.toString();
    const suffix = queryString ? `?${queryString}` : '';
    const response = await fetch(
      `${getApiBase(brokerageId)}/${providerId}/template${suffix}`,
      {
        method: 'PUT',
        credentials: 'include'
      }
    );
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to set custom template');
    }
    return response.json();
  },

  async update(
    brokerageId: string,
    providerId: string,
    data: Partial<BrokerageProvider>
  ): Promise<BrokerageProvider> {
    const response = await fetch(`${getApiBase(brokerageId)}/${providerId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.text();
      throw new Error(error || 'Failed to update provider configuration');
    }
    return response.json();
  },

  async delete(brokerageId: string, providerId: string): Promise<void> {
    const response = await fetch(`${getApiBase(brokerageId)}/${providerId}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    if (!response.ok) {
      throw new Error('Failed to delete provider configuration');
    }
  }
};
